import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LakshLogo, LakshMark } from '@/components/LakshLogo';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  AlertTriangle, 
  FileText, 
  Calculator, 
  Users, 
  Building2,
  Shield,
  Zap,
  Clock,
  CheckCircle2,
  XCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const questions = [
  {
    id: 'revenue',
    category: 'Business Profile',
    question: 'What is your annual revenue?',
    type: 'select',
    options: [
      { value: 'sub_20l', label: 'Under ₹20 Lakhs', score: 1 },
      { value: '20l_1cr', label: '₹20L - ₹1 Crore', score: 2 },
      { value: '1cr_5cr', label: '₹1Cr - ₹5 Crore', score: 3 },
      { value: 'above_5cr', label: 'Above ₹5 Crore', score: 4 },
    ]
  },
  {
    id: 'gst_registered',
    category: 'GST Compliance',
    question: 'Is your business GST registered?',
    type: 'select',
    options: [
      { value: 'yes', label: 'Yes, registered', score: 3 },
      { value: 'no', label: 'No, not required', score: 1 },
      { value: 'planning', label: 'Planning to register', score: 2 },
    ]
  },
  {
    id: 'gst_filing',
    category: 'GST Compliance',
    question: 'How do you currently file GST returns?',
    type: 'select',
    options: [
      { value: 'manual', label: 'Manual filing on GST portal', score: 1 },
      { value: 'ca', label: 'Through CA/tax consultant', score: 2 },
      { value: 'software', label: 'Using accounting software', score: 3 },
      { value: 'none', label: 'Not applicable', score: 0 },
    ]
  },
  {
    id: 'invoice_volume',
    category: 'Invoicing',
    question: 'How many invoices do you generate per month?',
    type: 'select',
    options: [
      { value: '1_10', label: '1-10 invoices', score: 1 },
      { value: '11_50', label: '11-50 invoices', score: 2 },
      { value: '51_200', label: '51-200 invoices', score: 3 },
      { value: 'above_200', label: 'Above 200 invoices', score: 4 },
    ]
  },
  {
    id: 'current_tool',
    category: 'Current Setup',
    question: 'What do you currently use for accounting?',
    type: 'select',
    options: [
      { value: 'excel', label: 'Excel/Google Sheets', score: 1 },
      { value: 'tally', label: 'Tally', score: 2 },
      { value: 'vyapar', label: 'Vyapar/Khatabook', score: 2 },
      { value: 'zoho', label: 'Zoho Books/QuickBooks', score: 3 },
      { value: 'none', label: 'No formal system', score: 0 },
    ]
  },
  {
    id: 'itc_tracking',
    category: 'ITC Management',
    question: 'How do you track Input Tax Credit (ITC)?',
    type: 'select',
    options: [
      { value: 'not_tracking', label: "Don't track ITC", score: 0 },
      { value: 'manual', label: 'Manual tracking', score: 1 },
      { value: 'ca_manages', label: 'CA manages it', score: 2 },
      { value: 'automated', label: 'Automated reconciliation', score: 3 },
    ]
  },
  {
    id: 'vendor_count',
    category: 'Operations',
    question: 'How many vendors/suppliers do you work with?',
    type: 'select',
    options: [
      { value: '1_5', label: '1-5 vendors', score: 1 },
      { value: '6_20', label: '6-20 vendors', score: 2 },
      { value: '21_50', label: '21-50 vendors', score: 3 },
      { value: 'above_50', label: 'Above 50 vendors', score: 4 },
    ]
  },
  {
    id: 'pain_points',
    category: 'Challenges',
    question: 'What is your biggest accounting challenge?',
    type: 'select',
    options: [
      { value: 'gst_compliance', label: 'GST compliance & filing', score: 3 },
      { value: 'invoicing', label: 'Creating professional invoices', score: 2 },
      { value: 'itc', label: 'ITC reconciliation & recovery', score: 3 },
      { value: 'collections', label: 'Payment collections', score: 2 },
      { value: 'visibility', label: 'Cash flow visibility', score: 2 },
    ]
  },
];

const getReadinessLevel = (score, maxScore) => {
  const percentage = (score / maxScore) * 100;
  if (percentage >= 75) return { level: 'Advanced', color: 'text-emerald-500', bg: 'bg-emerald-500' };
  if (percentage >= 50) return { level: 'Intermediate', color: 'text-amber-500', bg: 'bg-amber-500' };
  if (percentage >= 25) return { level: 'Beginner', color: 'text-orange-500', bg: 'bg-orange-500' };
  return { level: 'Getting Started', color: 'text-red-500', bg: 'bg-red-500' };
};

const getRecommendations = (answers) => {
  const recommendations = [];
  
  if (answers.gst_registered === 'planning' || answers.gst_registered === 'no') {
    recommendations.push({
      priority: 'high',
      title: 'GST Registration',
      description: 'If your turnover exceeds ₹40L (₹20L for services), GST registration is mandatory.',
      action: 'We\'ll help you set up GST-compliant invoicing from day one.'
    });
  }
  
  if (answers.itc_tracking === 'not_tracking' || answers.itc_tracking === 'manual') {
    recommendations.push({
      priority: 'high',
      title: 'ITC Recovery',
      description: 'You might be losing 15-20% of eligible ITC due to manual tracking.',
      action: 'Track ITC on every purchase in Laksh today; automatic GSTR-2B reconciliation is coming soon.'
    });
  }
  
  if (answers.current_tool === 'excel' || answers.current_tool === 'none') {
    recommendations.push({
      priority: 'medium',
      title: 'Digital Migration',
      description: 'Moving from Excel/manual to digital accounting will save 10+ hours/month.',
      action: 'Import your existing data in minutes with our migration wizard.'
    });
  }
  
  if (answers.pain_points === 'collections') {
    recommendations.push({
      priority: 'medium',
      title: 'Faster Collections',
      description: 'Late payments are hurting your cash flow; tracking receivables in one place is the first fix.',
      action: 'Track who owes you in one place today; WhatsApp delivery with payment links is coming soon.'
    });
  }
  
  if (answers.gst_filing === 'manual') {
    recommendations.push({
      priority: 'medium',
      title: 'GST Filing Discipline',
      description: 'Manual filing increases error risk and compliance penalties.',
      action: 'See every GSTR-1 and GSTR-3B deadline and share your books with your CA in Laksh.'
    });
  }
  
  // Add default recommendations
  if (recommendations.length < 3) {
    recommendations.push({
      priority: 'low',
      title: 'AI Compliance Co-Pilot',
      description: 'Get instant answers to GST, TDS, and compliance questions.',
      action: 'Arya, our AI assistant, is available 24/7 to help you.'
    });
  }
  
  return recommendations;
};

export default function MigrationAssessmentPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [contactInfo, setContactInfo] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  const totalSteps = questions.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const nextStep = () => {
    if (!answers[questions[currentStep].id]) {
      toast.error('Please select an option to continue');
      return;
    }
    
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const calculateScore = () => {
    let score = 0;
    let maxScore = 0;
    
    questions.forEach(q => {
      const answer = answers[q.id];
      if (answer) {
        const option = q.options.find(o => o.value === answer);
        if (option) {
          score += option.score;
        }
      }
      maxScore += Math.max(...q.options.map(o => o.score));
    });
    
    return { score, maxScore };
  };

  const handleSubmit = async () => {
    if (!contactInfo.email) {
      toast.error('Please enter your email');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        ...contactInfo,
        segment: 'migration_assessment',
        source: 'migration_assessment',
      });
      toast.success('Assessment submitted! We\'ll reach out with your personalized migration plan.');
      navigate('/signup');
    } catch (error) {
      if (error.response?.data?.detail?.includes('already')) {
        toast.info('You\'re already on our list! Let\'s get you started.');
        navigate('/signup');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const { score, maxScore } = calculateScore();
  const readiness = getReadinessLevel(score, maxScore);
  const recommendations = getRecommendations(answers);

  if (showResults) {
    return (
      <div className="min-h-screen bg-[#06100A]">
        {/* Header */}
        <header className="border-b border-white/10 bg-[#06100A]/90 backdrop-blur-md">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/">
              <LakshLogo height={36} />
            </Link>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-12">
          {/* Score Card */}
          <Card className="bg-gradient-to-br from-[#0a1a12] to-[#06100A] border-[#1D9E75]/30 mb-8">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="mb-6">
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-[#EF9F27]/10 border-4 border-[#EF9F27] mb-4">
                  <span className="text-4xl font-bold text-[#EF9F27] font-mono">{Math.round((score/maxScore)*100)}%</span>
                </div>
                <h2 className="text-3xl font-bold text-white font-['Outfit'] mb-2">
                  Your Readiness Score
                </h2>
                <p className={`text-xl font-semibold ${readiness.color}`}>
                  {readiness.level} Level
                </p>
              </div>
              
              <div className="max-w-md mx-auto">
                <Progress value={(score/maxScore)*100} className="h-3 bg-white/10" />
              </div>
            </CardContent>
          </Card>

          {/* Recommendations */}
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-white font-['Outfit'] mb-6">
              Your Personalized Action Plan
            </h3>
            <div className="space-y-4">
              {recommendations.map((rec, index) => (
                <Card key={index} className="bg-[#0a1a12] border-white/10">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        rec.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                        rec.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-emerald-500/20 text-emerald-400'
                      }`}>
                        {rec.priority === 'high' ? <AlertTriangle className="w-5 h-5" /> :
                         rec.priority === 'medium' ? <Clock className="w-5 h-5" /> :
                         <CheckCircle2 className="w-5 h-5" />}
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-1">{rec.title}</h4>
                        <p className="text-gray-400 mb-2">{rec.description}</p>
                        <p className="text-[#1D9E75] text-sm flex items-center gap-1">
                          <Zap className="w-4 h-4" /> {rec.action}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* What Laksh Offers */}
          <Card className="bg-[#0a1a12] border-[#EF9F27]/30 mb-8">
            <CardHeader>
              <CardTitle className="text-xl text-white font-['Outfit']">
                What Laksh Can Do For You
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { icon: FileText, text: 'GST-compliant invoicing in 60 seconds' },
                  { icon: Calculator, text: 'ITC tracking (GSTR-2B reconciliation coming soon)' },
                  { icon: Shield, text: 'AI compliance co-pilot for instant answers' },
                  { icon: Users, text: 'CA collaboration portal' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-300">
                    <div className="w-8 h-8 rounded-full bg-[#1D9E75]/20 flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-[#1D9E75]" />
                    </div>
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Contact Form */}
          <Card className="bg-gradient-to-br from-[#EF9F27]/10 to-[#1D9E75]/10 border-[#EF9F27]/30">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-white font-['Outfit'] mb-2 text-center">
                Get Your Migration Plan
              </h3>
              <p className="text-gray-400 text-center mb-6">
                Our team will create a personalized migration plan based on your assessment
              </p>
              
              <div className="max-w-md mx-auto space-y-4">
                <div>
                  <Label className="text-gray-300">Name</Label>
                  <Input
                    value={contactInfo.name}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Your name"
                    className="bg-[#06100A] border-white/20 text-white"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Email *</Label>
                  <Input
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="you@company.com"
                    className="bg-[#06100A] border-white/20 text-white"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Phone</Label>
                  <Input
                    value={contactInfo.phone}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+91 98765 43210"
                    className="bg-[#06100A] border-white/20 text-white"
                  />
                </div>
                
                <Button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full bg-[#EF9F27] hover:bg-[#EF9F27]/90 text-[#06100A] font-semibold h-12"
                  data-testid="submit-assessment-btn"
                >
                  {submitting ? 'Submitting...' : 'Get My Migration Plan & Start Free'}
                </Button>
                
                <p className="text-center text-gray-500 text-sm">
                  Free for businesses under ₹20L revenue. No credit card required.
                </p>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  const currentQuestion = questions[currentStep];

  return (
    <div className="min-h-screen bg-[#06100A]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#06100A]/90 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <LakshLogo height={36} />
          </Link>
          <span className="text-gray-400 text-sm">
            Migration Readiness Assessment
          </span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Question {currentStep + 1} of {totalSteps}</span>
            <span>{currentQuestion.category}</span>
          </div>
          <Progress value={progress} className="h-2 bg-white/10" />
        </div>

        {/* Question Card */}
        <Card className="bg-[#0a1a12] border-white/10 mb-8" data-testid="assessment-question">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-white font-['Outfit'] mb-8">
              {currentQuestion.question}
            </h2>
            
            <div className="space-y-3">
              {currentQuestion.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(currentQuestion.id, option.value)}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                    answers[currentQuestion.id] === option.value
                      ? 'border-[#EF9F27] bg-[#EF9F27]/10 text-white'
                      : 'border-white/10 hover:border-white/30 text-gray-300 hover:text-white'
                  }`}
                  data-testid={`option-${option.value}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      answers[currentQuestion.id] === option.value
                        ? 'border-[#EF9F27] bg-[#EF9F27]'
                        : 'border-gray-500'
                    }`}>
                      {answers[currentQuestion.id] === option.value && (
                        <Check className="w-3 h-3 text-[#06100A]" />
                      )}
                    </div>
                    <span className="font-medium">{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
            className="border-white/20 text-white hover:bg-white/10 gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Previous
          </Button>
          <Button
            onClick={nextStep}
            className="bg-[#EF9F27] hover:bg-[#EF9F27]/90 text-[#06100A] font-semibold gap-2"
            data-testid="next-question-btn"
          >
            {currentStep === totalSteps - 1 ? 'See Results' : 'Next'} <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </main>
    </div>
  );
}
